import { useEffect, useRef, useState } from 'react';

import { useCreateFeedback } from '@/features/Feedback/api';
import {
    FEEDBACK_IMPACT_LABELS,
    FEEDBACK_TYPE_LABELS,
    formatAffectedObject,
} from '@/features/Feedback/feedbackLabels';
import { FeedbackImpact, FeedbackType } from '@/features/Feedback/types';
import { useAttachmentUpload } from '@/features/Feedback/useAttachmentUpload';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from '@/components/ui/dialog';

import { captureFeedbackContext } from './captureFeedbackContext';
import {
    ContactField,
    RadioField,
    ReadOnlyField,
    TextField,
    UploadField,
} from './fields';
import { EMPTY_FORM_STATE, FormState, buildCreateArgs } from './formState';
import {
    RelatedDocumentsField,
    isDocumentSearchExpanded,
} from './relatedDocuments/RelatedDocumentsField';
import { useRelatedDocuments } from './relatedDocuments/useRelatedDocuments';
import { useAffectedObject } from './useAffectedObject';

const TYPE_OPTIONS: { value: FeedbackType; label: string; hint: string }[] = [
    {
        value: 'bug',
        label: FEEDBACK_TYPE_LABELS.bug,
        hint: 'Something does not work as expected',
    },
    {
        value: 'improvement',
        label: FEEDBACK_TYPE_LABELS.improvement,
        hint: 'Something works but could be clearer or faster',
    },
];

const IMPACT_OPTIONS: { value: FeedbackImpact; label: string; hint: string }[] =
    [
        {
            value: 'blocking',
            label: FEEDBACK_IMPACT_LABELS.blocking,
            hint: 'Work cannot continue',
        },
        {
            value: 'workaround_exists',
            label: FEEDBACK_IMPACT_LABELS.workaround_exists,
            hint: 'Work continues another way',
        },
        {
            value: 'cosmetic',
            label: FEEDBACK_IMPACT_LABELS.cosmetic,
            hint: 'Looks off or happens rarely',
        },
    ];

const STEPS_PLACEHOLDER = '1. Open the asset collection\n2. Select a document\n3. …';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/** The form mounts inside DialogContent, so its state and captured context reset on every open. */
export function SendFeedbackDialog({ open, onOpenChange }: Props) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="flex max-h-[90vh] max-w-[640px] flex-col gap-0 overflow-hidden rounded-xl p-0"
                data-testid="SendFeedbackDialog"
                onEscapeKeyDown={(event) => {
                    if (isDocumentSearchExpanded()) event.preventDefault();
                }}
            >
                <SendFeedbackForm onClose={() => onOpenChange(false)} />
            </DialogContent>
        </Dialog>
    );
}

function SendFeedbackForm({ onClose }: { onClose: () => void }) {
    const [context] = useState(() =>
        captureFeedbackContext(window.location, new Date()),
    );
    const affectedObject = useAffectedObject(context.target);
    const [state, setState] = useState<FormState>(EMPTY_FORM_STATE);
    const evidence = useAttachmentUpload('evidence');
    const related = useRelatedDocuments();
    const createFeedback = useCreateFeedback();
    // A form closed mid-send must not close the next one the customer opens.
    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const update = (patch: Partial<FormState>) =>
        setState((previous) => ({ ...previous, ...patch }));

    const args = buildCreateArgs(
        state,
        context,
        [...evidence.readyRefs, ...related.readyRefs],
        affectedObject,
    );
    const canSubmit =
        args !== null &&
        !createFeedback.isPending &&
        !evidence.isUploading &&
        !related.isCopying;

    const handleSubmit = () => {
        if (!args || !canSubmit) return;
        toast.promise(
            createFeedback.mutateAsync(args).then((row) => {
                if (isMounted.current) onClose();
                return row;
            }),
            {
                loading: 'Sending feedback…',
                success: 'Thanks for your feedback!',
                error: 'Failed to send feedback.',
            },
        );
    };

    return (
        <>
            <div className="border-b px-6 pb-6 pt-6 pr-16">
                <DialogTitle className="text-xl font-bold">
                    Send feedback
                </DialogTitle>
                <DialogDescription>
                    Report a bug or suggest an improvement.
                </DialogDescription>
            </div>

            <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-6">
                <RadioField
                    label="Feedback type"
                    idPrefix="feedback-type"
                    options={TYPE_OPTIONS}
                    value={state.feedbackType}
                    onChange={(feedbackType) => update({ feedbackType })}
                    className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                />
                <TextField
                    id="feedback-expected"
                    label="Expected result"
                    placeholder="What should have happened?"
                    value={state.expectedResult}
                    onChange={(expectedResult) => update({ expectedResult })}
                />
                <TextField
                    id="feedback-actual"
                    label="Actual result"
                    placeholder="What happened instead?"
                    value={state.actualResult}
                    onChange={(actualResult) => update({ actualResult })}
                />
                {state.feedbackType === 'bug' && (
                    <TextField
                        id="feedback-steps"
                        label="Steps to reproduce"
                        optional
                        placeholder={STEPS_PLACEHOLDER}
                        value={state.stepsToReproduce}
                        onChange={(stepsToReproduce) =>
                            update({ stepsToReproduce })
                        }
                    />
                )}
                <RadioField
                    label="Impact"
                    idPrefix="feedback-impact"
                    options={IMPACT_OPTIONS}
                    value={state.impact}
                    onChange={(impact) => update({ impact })}
                    className="grid grid-cols-1 gap-2 sm:grid-cols-3"
                />
                <UploadField
                    label="Screenshot or screen recording"
                    hint="PNG, JPG, MP4 or MOV"
                    upload={evidence}
                />
                <RelatedDocumentsField related={related} />
                <div className="space-y-6 border-t pt-6">
                    {affectedObject && (
                        <ReadOnlyField
                            id="feedback-affected-object"
                            label="Affected object"
                            value={formatAffectedObject(affectedObject)}
                        />
                    )}
                    <ReadOnlyField
                        id="feedback-page-url"
                        label="Where it happened"
                        value={context.pageUrl}
                    />
                    <ContactField />
                </div>
            </div>

            <div className="flex justify-end gap-3 border-t bg-slate-50 px-6 py-4">
                <Button variant="outline" className="h-11" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    className="h-11"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                >
                    Send feedback
                </Button>
            </div>
        </>
    );
}
